import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Download, RotateCcw, CheckCircle2, XCircle, FileText, Send, User, Play } from 'lucide-react';
import { exportMailMergeToCSV, type MailMergeResult, type OutputType } from './mailmergeExport';
import MailMergeResultsTable from './MailMergeResultsTable';
import { Progress } from '@/components/ui/progress';
import MailMergeToCampaignDialog from '@/components/campaigns/MailMergeToCampaignDialog';
import { useCampaignTemplates } from '@/hooks/useCampaignTemplates';

interface MailMergeViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  run: {
    id: string;
    name: string;
    outreach_type: string;
    output_type: string;
    custom_prompt: string | null;
    status: string;
    total_count: number;
    success_count: number;
    failed_count: number;
    results: any;
    lead_ids?: string[];
    sender_id?: string | null;
    template_id?: string | null;
    template_mode?: string | null;
  };
  onRunUpdated: (run: any) => void;
}

interface TeamMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  work_email: string | null;
  work_phone: string | null;
  contact_number: string | null;
}

const MailMergeViewDialog: React.FC<MailMergeViewDialogProps> = ({ open, onOpenChange, run, onRunUpdated }) => {
  const { profile } = useAuth();
  const [retrying, setRetrying] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [retryProgress, setRetryProgress] = useState(0);
  const abortRef = useRef(false);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedSenderId, setSelectedSenderId] = useState<string>(run.sender_id || profile?.id || '');
  const [companyName, setCompanyName] = useState<string>('');

  const { data: campaignTemplates } = useCampaignTemplates(profile?.company_id ?? undefined);

  useEffect(() => {
    if (!open || !profile?.company_id) return;
    supabase
      .from('profiles')
      .select('id, first_name, last_name, job_title, work_email, work_phone, contact_number')
      .eq('company_id', profile.company_id)
      .then(({ data }) => {
        if (data) setTeamMembers(data);
      });
    supabase
      .from('companies')
      .select('name')
      .eq('id', profile.company_id)
      .single()
      .then(({ data }) => {
        if (data) setCompanyName(data.name);
      });
    setSelectedSenderId(run.sender_id || profile.id);
  }, [open, profile?.company_id, profile?.id, run.sender_id]);

  const resultsArray: MailMergeResult[] = Array.isArray(run.results) ? run.results : [];
  const resultsMap = new Map<string, MailMergeResult>();
  resultsArray.forEach((r) => resultsMap.set(r.leadId, r));

  const [liveResults, setLiveResults] = useState<Map<string, MailMergeResult>>(resultsMap);

  // Sync liveResults when run prop changes
  useEffect(() => {
    const newMap = new Map<string, MailMergeResult>();
    const arr: MailMergeResult[] = Array.isArray(run.results) ? run.results : [];
    arr.forEach((r) => newMap.set(r.leadId, r));
    setLiveResults(newMap);
  }, [run.results]);

  const successCount = Array.from(liveResults.values()).filter((r) => r.status === 'success').length;
  const failedCount = Array.from(liveResults.values()).filter((r) => r.status === 'failed').length;
  const pendingCount = Array.from(liveResults.values()).filter((r) => r.status === 'pending' || r.status === 'generating').length;
  const totalCount = liveResults.size;

  // Determine if we can continue (in_progress or partial with pending leads from lead_ids)
  const canContinue = useMemo(() => {
    if (run.status === 'in_progress') return true;
    if (!run.lead_ids || run.lead_ids.length === 0) return false;
    // Check if there are lead_ids not present in results or with pending status
    const processedIds = new Set(Array.from(liveResults.keys()));
    const unprocessed = run.lead_ids.filter(id => !processedIds.has(id));
    return unprocessed.length > 0 || pendingCount > 0;
  }, [run.status, run.lead_ids, liveResults, pendingCount]);

  const handleExport = useCallback(() => {
    exportMailMergeToCSV(liveResults, run.outreach_type, run.output_type as OutputType);
  }, [liveResults, run.outreach_type, run.output_type]);

  const getSenderInfo = useCallback(() => {
    const sender = teamMembers.find(m => m.id === selectedSenderId) ||
      (profile ? { first_name: profile.first_name, last_name: profile.last_name, job_title: profile.job_title, work_email: profile.work_email, work_phone: profile.work_phone, contact_number: (profile as any)?.contact_number } : null);
    return {
      senderName: sender ? `${sender.first_name || ''} ${sender.last_name || ''}`.trim() : '',
      senderRole: sender?.job_title || 'Sales Representative',
      senderCompany: companyName || 'Our Company',
      senderEmail: (sender as any)?.work_email || '',
      senderPhone: (sender as any)?.work_phone || '',
      senderContactNumber: (sender as any)?.contact_number || '',
    };
  }, [teamMembers, selectedSenderId, profile, companyName]);

  const fetchCompanyData = useCallback(async () => {
    if (!profile?.company_id) return null;
    const { data } = await supabase
      .from('companies')
      .select('name, description, web_search_data, industry, website, address, city, country, outreach_instructions')
      .eq('id', profile.company_id)
      .single();
    if (!data) return null;
    return {
      name: data.name,
      summary: data.description,
      address: (data as any).address,
      city: (data as any).city,
      country: (data as any).country,
      outreachInstructions: (data as any).outreach_instructions || null,
      ...(data.web_search_data && typeof data.web_search_data === 'object' ? data.web_search_data as Record<string, unknown> : {}),
    };
  }, [profile?.company_id]);

  const updateRunInDb = useCallback(async (resultsMap: Map<string, MailMergeResult>, isFinal: boolean, wasAborted: boolean) => {
    const arr = Array.from(resultsMap.values());
    const sc = arr.filter(r => r.status === 'success').length;
    const fc = arr.filter(r => r.status === 'failed').length;
    const pending = arr.filter(r => r.status === 'pending' || r.status === 'generating').length;
    const status = isFinal
      ? (wasAborted ? 'partial' : (fc === 0 ? 'completed' : 'partial'))
      : (pending > 0 ? 'in_progress' : (fc === 0 ? 'completed' : 'partial'));

    const { error } = await supabase
      .from('mailmerge_runs' as any)
      .update({
        results: arr,
        success_count: sc,
        failed_count: fc,
        status,
        updated_at: new Date().toISOString(),
      } as any)
      .eq('id', run.id);

    if (!error) {
      onRunUpdated({ ...run, results: arr, success_count: sc, failed_count: fc, status });
    }
    return error;
  }, [run, onRunUpdated]);

  const handleContinueGeneration = useCallback(async () => {
    if (!run.lead_ids || run.lead_ids.length === 0) {
      toast.error('No lead IDs stored for this run');
      return;
    }

    // Find leads that still need processing
    const processedSuccessIds = new Set(
      Array.from(liveResults.entries())
        .filter(([_, r]) => r.status === 'success')
        .map(([id]) => id)
    );
    const pendingLeadIds = run.lead_ids.filter(id => !processedSuccessIds.has(id));

    if (pendingLeadIds.length === 0) {
      toast.info('All leads have been processed successfully');
      return;
    }

    setContinuing(true);
    setRetryProgress(0);
    abortRef.current = false;

    // Look up template if we have a template_id
    const template = campaignTemplates?.find(t => t.id === run.template_id);

    const senderInfo = getSenderInfo();
    const companyData = await fetchCompanyData();

    const batchSize = 10;
    let offset = 0;
    const currentResults = new Map(liveResults);

    // Initialize pending entries for leads not yet in results
    for (const id of pendingLeadIds) {
      if (!currentResults.has(id)) {
        currentResults.set(id, {
          leadId: id,
          leadName: '',
          leadEmail: '',
          leadPhone: '',
          leadAddress: '',
          leadWebsite: '',
          leadCategory: '',
          leadStatus: '',
          status: 'generating',
        });
      } else {
        const existing = currentResults.get(id)!;
        currentResults.set(id, { ...existing, status: 'generating', error: undefined });
      }
    }
    setLiveResults(new Map(currentResults));

    let totalProcessed = 0;

    while (offset < pendingLeadIds.length && !abortRef.current) {
      try {
        const { data, error } = await supabase.functions.invoke('generate-mailmerge-batch', {
          body: {
            lead_ids: pendingLeadIds,
            outreach_type: template?.name || run.outreach_type,
            custom_prompt: run.custom_prompt || '',
            sender_info: senderInfo,
            company_data: companyData,
            output_type: run.output_type,
            ...(template ? {
              template_content: {
                subject: template.subject_template,
                body: template.body_template,
                name: template.name,
              },
              template_mode: run.template_mode || (template as any).template_mode || 'guide',
            } : {}),
            offset,
            batch_size: batchSize,
          },
        });

        if (error) throw new Error(error.message || 'Edge function error');
        if (data?.error) throw new Error(data.error);

        const batchResults = data?.results || [];
        for (const r of batchResults) {
          const existing = currentResults.get(r.leadId);
          if (r.success) {
            currentResults.set(r.leadId, {
              ...(existing || { leadId: r.leadId, leadName: '', leadEmail: '', leadPhone: '', leadAddress: '', leadWebsite: '', leadCategory: '', leadStatus: '' }),
              status: 'success',
              subject: r.email?.subject,
              emailBody: r.email?.body,
              callScript: r.callScript,
              ...(r.recipientEmail ? { leadEmail: r.recipientEmail } : {}),
              ...(r.recipientContactName ? { contactName: r.recipientContactName } : {}),
              error: undefined,
            });
          } else {
            currentResults.set(r.leadId, {
              ...(existing || { leadId: r.leadId, leadName: '', leadEmail: '', leadPhone: '', leadAddress: '', leadWebsite: '', leadCategory: '', leadStatus: '' }),
              status: 'failed',
              error: r.error || 'Unknown error',
            });
          }
          totalProcessed++;
        }

        setLiveResults(new Map(currentResults));
        setRetryProgress(Math.round((totalProcessed / pendingLeadIds.length) * 100));

        // Incremental save
        await updateRunInDb(currentResults, false, false);

        if (data?.done) break;
        offset = data?.nextOffset ?? (offset + batchSize);
      } catch (err: any) {
        const batchIds = pendingLeadIds.slice(offset, offset + batchSize);
        for (const id of batchIds) {
          const existing = currentResults.get(id);
          if (existing && (existing.status === 'generating' || existing.status === 'pending')) {
            currentResults.set(id, { ...existing, status: 'failed', error: err.message || 'Batch failed' });
            totalProcessed++;
          }
        }
        setLiveResults(new Map(currentResults));
        setRetryProgress(Math.round((totalProcessed / pendingLeadIds.length) * 100));
        await updateRunInDb(currentResults, false, false);
        offset += batchSize;
      }
    }

    // Final save
    const finalErr = await updateRunInDb(currentResults, true, abortRef.current);
    if (!finalErr) {
      toast.success('Continue generation complete');
    } else {
      toast.error('Failed to save results');
    }
    setContinuing(false);
  }, [run, liveResults, getSenderInfo, fetchCompanyData, updateRunInDb, campaignTemplates]);

  const handleRetryFailed = useCallback(async () => {
    const failedEntries = Array.from(liveResults.values()).filter((r) => r.status === 'failed');
    if (failedEntries.length === 0) return;

    const failedIds = failedEntries.map((r) => r.leadId);

    setRetrying(true);
    setRetryProgress(0);
    abortRef.current = false;

    // Reset failed entries to generating
    const currentResults = new Map(liveResults);
    failedEntries.forEach((r) => {
      currentResults.set(r.leadId, { ...r, status: 'generating', error: undefined });
    });
    setLiveResults(new Map(currentResults));

    const template = campaignTemplates?.find(t => t.id === run.template_id);
    const senderInfo = getSenderInfo();
    const companyData = await fetchCompanyData();

    const batchSize = 10;
    let offset = 0;
    let totalProcessed = 0;

    while (offset < failedIds.length && !abortRef.current) {
      try {
        const { data, error } = await supabase.functions.invoke('generate-mailmerge-batch', {
          body: {
            lead_ids: failedIds,
            outreach_type: template?.name || run.outreach_type,
            custom_prompt: run.custom_prompt || '',
            sender_info: senderInfo,
            company_data: companyData,
            output_type: run.output_type,
            ...(template ? {
              template_content: {
                subject: template.subject_template,
                body: template.body_template,
                name: template.name,
              },
              template_mode: run.template_mode || (template as any).template_mode || 'guide',
            } : {}),
            offset,
            batch_size: batchSize,
          },
        });

        if (error) throw new Error(error.message || 'Edge function error');
        if (data?.error) throw new Error(data.error);

        const batchResults = data?.results || [];
        for (const r of batchResults) {
          const existing = currentResults.get(r.leadId);
          if (r.success) {
            currentResults.set(r.leadId, {
              ...(existing || { leadId: r.leadId, leadName: '', leadEmail: '', leadPhone: '', leadAddress: '', leadWebsite: '', leadCategory: '', leadStatus: '' }),
              status: 'success',
              subject: r.email?.subject,
              emailBody: r.email?.body,
              callScript: r.callScript,
              ...(r.recipientEmail ? { leadEmail: r.recipientEmail } : {}),
              ...(r.recipientContactName ? { contactName: r.recipientContactName } : {}),
              error: undefined,
            });
          } else {
            currentResults.set(r.leadId, {
              ...(existing || { leadId: r.leadId, leadName: '', leadEmail: '', leadPhone: '', leadAddress: '', leadWebsite: '', leadCategory: '', leadStatus: '' }),
              status: 'failed',
              error: r.error || 'Unknown error',
            });
          }
          totalProcessed++;
        }

        setLiveResults(new Map(currentResults));
        setRetryProgress(Math.round((totalProcessed / failedIds.length) * 100));
        await updateRunInDb(currentResults, false, false);

        if (data?.done) break;
        offset = data?.nextOffset ?? (offset + batchSize);
      } catch (err: any) {
        const batchIds = failedIds.slice(offset, offset + batchSize);
        for (const id of batchIds) {
          const existing = currentResults.get(id);
          if (existing && existing.status === 'generating') {
            currentResults.set(id, { ...existing, status: 'failed', error: err.message || 'Batch failed' });
            totalProcessed++;
          }
        }
        setLiveResults(new Map(currentResults));
        setRetryProgress(Math.round((totalProcessed / failedIds.length) * 100));
        await updateRunInDb(currentResults, false, false);
        offset += batchSize;
      }
    }

    const finalErr = await updateRunInDb(currentResults, true, abortRef.current);
    if (!finalErr) {
      toast.success('Retry complete and saved');
    } else {
      toast.error('Failed to save updated results');
    }
    setRetrying(false);
  }, [liveResults, run, getSenderInfo, fetchCompanyData, updateRunInDb, campaignTemplates]);

  const isProcessing = retrying || continuing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-base">{run.name}</DialogTitle>
          <DialogDescription>
            <Badge variant="outline" className="mr-2">{run.outreach_type}</Badge>
            <Badge variant="outline">{run.output_type}</Badge>
            {run.status === 'in_progress' && (
              <Badge className="ml-2 bg-blue-500/10 text-blue-600 border-blue-500/30">In Progress</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40 border border-border/60">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-lg font-bold">{totalCount}</div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <div>
                  <div className="text-lg font-bold text-green-600">{successCount}</div>
                  <div className="text-xs text-muted-foreground">Success</div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                <XCircle className="h-4 w-4 text-destructive" />
                <div>
                  <div className="text-lg font-bold text-destructive">{failedCount}</div>
                  <div className="text-xs text-muted-foreground">Failed</div>
                </div>
              </div>
            </div>

            {teamMembers.length > 1 && (
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" /> Retry As
                </label>
                <Select value={selectedSenderId} onValueChange={setSelectedSenderId}>
                  <SelectTrigger className="w-full max-w-xs">
                    <SelectValue placeholder="Select sender" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {`${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Unnamed'}
                        {m.job_title ? ` — ${m.job_title}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {isProcessing && (
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">
                  {continuing ? 'Continuing generation…' : 'Retrying failed leads…'} {retryProgress}%
                </div>
                <Progress value={retryProgress} className="h-2" />
              </div>
            )}

            <MailMergeResultsTable results={liveResults} outputType={run.output_type as OutputType} />
          </div>
        </DialogBody>

        <DialogFooter>
          <div className="flex gap-2 flex-wrap">
            {canContinue && !isProcessing && (
              <Button variant="default" onClick={handleContinueGeneration} className="gap-1.5">
                <Play className="h-4 w-4" /> Continue Generation
                {pendingCount > 0 && ` (${pendingCount})`}
              </Button>
            )}
            {failedCount > 0 && !isProcessing && (
              <Button variant="outline" onClick={handleRetryFailed} className="gap-1.5">
                <RotateCcw className="h-4 w-4" /> Retry Failed ({failedCount})
              </Button>
            )}
            {successCount > 0 && (run.output_type === 'email' || run.output_type === 'both') && (
              <Button variant="outline" onClick={() => setShowCampaignDialog(true)} className="gap-1.5">
                <Send className="h-4 w-4" /> Send as Campaign
              </Button>
            )}
            <Button onClick={handleExport} className="gap-1.5">
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {showCampaignDialog && (
        <MailMergeToCampaignDialog
          open={showCampaignDialog}
          onOpenChange={setShowCampaignDialog}
          run={run}
        />
      )}
    </Dialog>
  );
};

export default MailMergeViewDialog;
